/**
 * ErrorBoundary Component
 * Catches React rendering errors and logs them for debugging
 */

import React, { Component } from 'react';
import { logErrorToBackend } from '../utils/errorLogger';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to the backend
    const componentStack = errorInfo?.componentStack || '';
    const pageName = this.props.pageName || window.location.pathname;

    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', componentStack);

    // Log to backend for admin visibility
    logErrorToBackend({
      message: error?.message || 'React component error',
      stack: error?.stack || '',
      component: this.extractComponentName(componentStack),
      page: pageName,
      action: 'Component rendering',
      details: {
        componentStack: componentStack,
        errorName: error?.name,
        errorType: 'React ErrorBoundary'
      }
    });

    this.setState({ errorInfo });
  }

  extractComponentName(componentStack) {
    // Extract the first component name from the stack
    if (!componentStack) return 'Unknown';
    const match = componentStack.match(/at (\w+)/);
    return match ? match[1] : 'Unknown';
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.icon}>&#x26A0;</div>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              We've logged this error and will look into it. You can try refreshing the page or going back.
            </p>
            {this.state.error && (
              <div style={styles.errorBox}>
                <strong>Error:</strong> {this.state.error.message}
              </div>
            )}
            <div style={styles.actions}>
              <button
                onClick={this.handleRetry}
                style={styles.button}
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                style={styles.secondaryButton}
              >
                Refresh Page
              </button>
            </div>
            <p style={styles.hint}>
              If this keeps happening, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    padding: '20px',
    backgroundColor: 'var(--color-bg-subtle, #f5f5f5)'
  },
  card: {
    backgroundColor: 'var(--color-bg-base, white)',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  icon: {
    fontSize: '48px',
    marginBottom: '16px',
    color: '#f59e0b'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: 'var(--color-text-primary, #333)',
    marginBottom: '12px'
  },
  message: {
    fontSize: '14px',
    color: 'var(--color-text-secondary, #666)',
    marginBottom: '20px',
    lineHeight: '1.5'
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '20px',
    fontSize: '13px',
    color: '#991b1b',
    textAlign: 'left',
    wordBreak: 'break-word'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginBottom: '20px'
  },
  button: {
    padding: '10px 20px',
    backgroundColor: 'var(--color-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  secondaryButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary, #666)',
    border: '1px solid var(--color-border-default, #ddd)',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  hint: {
    fontSize: '12px',
    color: 'var(--color-text-tertiary, #999)'
  }
};

export default ErrorBoundary;
